import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Bookmark, Trash, MagnifyingGlass, ArrowRight, BellRinging, Broadcast, Sparkle } from "@phosphor-icons/react";

export default function SavedSearches() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(null);
  const [injecting, setInjecting] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/saved-searches");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    await api.delete(`/saved-searches/${id}`);
    setItems(items.filter((i) => i.id !== id));
    toast.success("Recherche supprimée");
  };

  const toggleAlerts = async (id, enabled) => {
    setItems(items.map((i) => (i.id === id ? { ...i, alerts_enabled: enabled } : i)));
    try {
      await api.patch(`/saved-searches/${id}/alerts`, { enabled });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const check = async (id) => {
    setChecking(id);
    try {
      const { data } = await api.post(`/saved-searches/${id}/check`);
      if (data.new_count > 0) {
        const noteMap = { sent: " · email envoyé", "log-only": " · email (mode démo)", error: " · email en attente (domaine à vérifier)" };
        const emailNote = noteMap[data.email_status] || "";
        toast.success(`${data.new_count} nouveau${data.new_count > 1 ? "x" : ""} signal détecté${data.new_count > 1 ? "s" : ""}${emailNote} — ouverture des résultats…`);
        // A4 : ouvre la recherche avec SES filtres stockés (type d'opportunité inclus) et liste TOUS les signaux.
        navigate(`/app?saved=${id}`);
        return;
      }
      toast.info("Aucun nouveau signal trouvé pour cette recherche.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setChecking(null);
    }
  };

  const injectDemo = async () => {
    setInjecting(true);
    try {
      const { data } = await api.post("/demo/new-signal");
      toast.success(`Nouveau signal injecté : ${data.signal.role_title} @ ${data.company}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setInjecting(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="overline mb-2">Vos recherches & alertes</div>
          <h1 className="font-display text-3xl font-bold">Recherches sauvegardées</h1>
          <p className="text-muted text-sm mt-1">Activez les alertes pour être notifié par email dès qu'un nouveau signal correspond.</p>
        </div>
        <button onClick={injectDemo} disabled={injecting} data-testid="inject-signal-btn"
          className="flex items-center gap-2 text-sm border border-accent/30 text-accent rounded-md px-4 py-2.5 hover-lift disabled:opacity-60">
          <Sparkle size={15} weight="fill" /> {injecting ? "Injection…" : "Simuler un nouveau signal marché"}
        </button>
      </div>

      {loading ? (
        <div className="text-muted font-mono text-sm animate-pulse">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="surface rounded-lg p-10 text-center" data-testid="saved-empty">
          <Bookmark size={40} className="text-muted mx-auto mb-3" />
          <p className="text-muted mb-5">Aucune recherche sauvegardée pour l'instant.</p>
          <button onClick={() => navigate("/app")} className="bg-accent text-black font-semibold px-5 py-2.5 rounded-md hover-lift inline-flex items-center gap-2">
            <MagnifyingGlass size={16} /> Lancer une recherche
          </button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="saved-list">
          {items.map((it) => (
            <div key={it.id} className="surface rounded-lg p-5 hover-lift" data-testid={`saved-item-${it.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className="text-fg text-sm mb-2">{it.query}</p>
                  <div className="flex flex-wrap gap-1.5 mb-1">
                    {(it.criteria?.skills || []).slice(0, 6).map((s) => (
                      <span key={s} className="text-[0.65rem] font-mono bg-surface-2 text-muted border border-border px-2 py-0.5 rounded">{s}</span>
                    ))}
                  </div>
                  <div className="text-xs text-muted font-mono mt-2 flex items-center gap-1">
                    <Broadcast size={12} className="text-accent" /> {it.match_count} signal{it.match_count > 1 ? "aux" : ""} suivi{it.match_count > 1 ? "s" : ""}
                  </div>
                </div>
                <button onClick={() => remove(it.id)} className="p-2 rounded-md text-muted hover:text-destructive hover:bg-surface-2 shrink-0" data-testid={`delete-saved-${it.id}`}>
                  <Trash size={16} />
                </button>
              </div>

              <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-border">
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <Switch checked={!!it.alerts_enabled} onCheckedChange={(v) => toggleAlerts(it.id, v)} data-testid={`alerts-switch-${it.id}`} />
                  <span className="text-sm text-fg flex items-center gap-1.5"><BellRinging size={15} className={it.alerts_enabled ? "text-accent" : "text-muted"} weight={it.alerts_enabled ? "fill" : "regular"} /> Alertes email</span>
                </label>
                <button onClick={() => check(it.id)} disabled={checking === it.id} data-testid={`check-alerts-${it.id}`}
                  className="flex items-center gap-1.5 text-sm text-muted hover:text-fg disabled:opacity-60">
                  {checking === it.id ? "Vérification…" : <>Vérifier les nouveaux signaux <ArrowRight size={14} /></>}
                </button>
                <button onClick={() => navigate(`/app?saved=${it.id}`)} data-testid={`open-saved-${it.id}`}
                  className="flex items-center gap-1.5 text-sm text-accent hover:underline">
                  Ouvrir les résultats <ArrowRight size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
